import { Coins, Lock } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router"

import { useSidebar, SidebarMenuButton } from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"

import { SETTINGS_ENDPOINTS } from "@/constants"
import type { BillingOverview } from "@/hooks/api"

const LOW_BALANCE_RATIO = 0.1

const formatCredits = (value: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.max(0, value))

export interface SidebarCreditBalanceProps {
  overview?: BillingOverview | null
  isOpen?: boolean
}

const SidebarCreditBalance = ({
  overview,
  isOpen: isOpenProp,
}: SidebarCreditBalanceProps) => {
  const { t } = useTranslation()
  const { isMobile, open } = useSidebar()
  const isOpen = isOpenProp ?? (isMobile ? true : open)

  if (!overview) return null

  const balance = Math.max(0, overview.creditsBalance)
  const extraBalance = Math.max(0, overview.extraCreditsBalance)
  // The backend spends monthly credits first, then extra credits, and only
  // locks when the combined balance hits 0 — so warnings must consider both.
  const totalBalance = balance + extraBalance
  const limit = overview.monthlyCreditsLimit ?? 0
  const hasLimit = limit > 0
  const remainingRatio = hasLimit ? Math.min(1, balance / limit) : 1
  const remainingPercent = Math.round(remainingRatio * 100)
  const isLocked = Boolean(overview.lockedAt)
  const isLow =
    !isLocked && hasLimit && totalBalance < limit * LOW_BALANCE_RATIO
  // Monthly quota is spent but extra credits keep the org running — explains
  // why the bar can read empty without a low/locked warning.
  const isUsingExtra = !isLocked && balance === 0 && extraBalance > 0
  const needsAttention = isLocked || isLow

  const balanceLabel = hasLimit
    ? t("billing.creditIndicator.value", {
        balance: formatCredits(balance),
        limit: formatCredits(limit),
      })
    : formatCredits(balance)

  if (!isOpen) {
    return (
      <SidebarMenuButton
        tooltip={t("billing.creditIndicator.collapsedTooltip", {
          balance: formatCredits(totalBalance),
        })}
        size="sm"
        className={cn(
          "justify-center px-0",
          needsAttention
            ? "text-amber-600 hover:text-amber-600 dark:text-amber-400 dark:hover:text-amber-400"
            : "text-muted-foreground hover:text-sidebar-accent-foreground"
        )}
        asChild
      >
        <Link
          to={SETTINGS_ENDPOINTS.organizationPlan}
          data-testid="sidebar-credit-balance-collapsed"
          aria-label={t("billing.creditIndicator.viewPlan")}
        >
          {isLocked ? (
            <Lock className="size-3.5" />
          ) : (
            <Coins className="size-3.5" />
          )}
        </Link>
      </SidebarMenuButton>
    )
  }

  return (
    <Link
      to={SETTINGS_ENDPOINTS.organizationPlan}
      data-testid="sidebar-credit-balance"
      aria-label={t("billing.creditIndicator.viewPlan")}
      className="block rounded-md border border-sidebar-border/70 bg-sidebar-accent/30 px-3 py-2 transition-colors hover:bg-sidebar-accent/70"
    >
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="flex items-center gap-1.5 text-sidebar-foreground/70">
          <Coins className="size-3.5" />
          {t("billing.creditIndicator.title")}
        </span>
        <span
          className={cn(
            "font-medium",
            needsAttention
              ? "text-amber-600 dark:text-amber-400"
              : "text-sidebar-foreground"
          )}
        >
          {balanceLabel}
        </span>
      </div>
      {hasLimit && (
        <div
          role="progressbar"
          aria-valuenow={remainingPercent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t("billing.creditIndicator.title")}
          className="mt-2 h-1.5 w-full rounded-full bg-sidebar-border/60"
        >
          <div
            className={cn(
              "h-1.5 rounded-full transition-all",
              needsAttention ? "bg-amber-500" : "bg-emerald-500"
            )}
            style={{ width: `${remainingPercent}%` }}
          />
        </div>
      )}
      {extraBalance > 0 && (
        <div className="mt-1.5 text-xs text-sidebar-foreground/60">
          {t("billing.creditIndicator.extraCredits", {
            balance: formatCredits(extraBalance),
          })}
        </div>
      )}
      {isUsingExtra && (
        <div className="mt-1.5 text-xs font-medium text-sidebar-foreground/70">
          {t("billing.creditIndicator.usingExtra")}
        </div>
      )}
      {isLocked && (
        <div className="mt-1.5 flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400">
          <Lock className="size-3" />
          {t("billing.creditIndicator.locked")}
        </div>
      )}
      {isLow && (
        <div className="mt-1.5 text-xs font-medium text-amber-600 dark:text-amber-400">
          {t("billing.creditIndicator.lowBalance")}
        </div>
      )}
    </Link>
  )
}

export default SidebarCreditBalance
